import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { button, icon, kendo, label, number, quote, route } from "../../../config";
import { addQueueProject, createProject } from "../../../shared/services/projects.service";
import { sliceString } from "../../../utils";
import { createDynamicTeamName } from "../../../utils/common";
import { filterData, onBlur, onFocus, onOpen } from "../../../utils/kendo";
import { trackCreateProject } from "../../../utils/analytics";
import { getDefaultTaskValues } from "../../Tasks/tasks.service";

const CreateProjectPopup = (props) => {
  const { anchor, show, setShow, queueId, fromHeader } = props;
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [queue, setQueue] = useState(null);
  const [queueError, setQueueError] = useState("");
  const [projectCode, setProjectCode] = useState("");
  const deleteRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const allQueues = useSelector((state) => state.allQueues);
  const [filteredList, setFilteredList] = useState([]);
  const operationalTeamName = useSelector((state) => state.auth.user.operationalTeamName);
  const location = useLocation();
  const path = location.pathname;
  const queuePagePath = useMemo(() => {
    return path === route.PRIVATE_ROUTE.QUEUES.PATH;
  }, [path]);

  useEffect(() => {
    setFilteredList(allQueues?.sort((a, b) => a.QueueName.trim().localeCompare(b.QueueName.trim()))?.filter((q) => q.RoleId));
  }, [allQueues]);

  useEffect(() => {
    if (show) setTimeout(() => inputRef?.current?.focus(), number.FOUR_HUNDRED);
  }, [show]);

  /**
   * used to set value of projectName
   * @param {*} event
   */
  const handleProjectName = (e) => {
    const projectName = e.target.value;
    setName(projectName);
    setProjectCode(sliceString(projectName, number.FOUR).toUpperCase());
    if (!projectName) {
      setNameError(quote.REQUIRED_FIELD);
    } else if (projectName && projectName.replace(/ /g, "").length < number.FOUR) {
      setNameError(createDynamicTeamName(quote.PROJECT_NAME_LENGTH_ERROR, user.operationalTeamName));
    } else {
      setNameError("");
    }
  };

  /**
   * used to set value of queue
   * @param {*} event
   */
  const handleQueueChange = (e) => {
    const queue = e.target.value;
    setQueue(queue);
    if (!queue?.QueueId) {
      setQueueError(quote.REQUIRED_FIELD);
    } else {
      setQueueError("");
    }
  };

  /**
   * creates project
   * @param {*} event
   */
  const handleCreateProject = async (e ) => {
  e.preventDefault();
    const payload = { userId: user.id, companyId: user.companyId, name, projectCode, queue: queueId ? queueId : queue?.QueueId, workspacePage: queueId };
   const projectId = await dispatch(createProject(payload, history));
   if (projectId) {
      trackCreateProject({ project_id: projectId, project_name: name });
    }
   setShow(false);
    if (props.workspacePage) {
      await dispatch(
        addQueueProject({
          ProjectId: projectId,
          ProjectName: name,
            Privacy: number.TWO,
          })
        );
    }
   else  if (queuePagePath) {
    await dispatch(getDefaultTaskValues(user.companyId, user.id));
    }

  };

  const hideOnBlur = () => {
    setShow(false);
  };

  const filterChangeForWorkSpace = async (event) => {
    const filteredListWorkspace = filterData(
      event.filter,
      allQueues?.sort((a, b) => a.QueueName.trim().localeCompare(b.QueueName.trim()))?.filter((q) => q.RoleId)
    );
    setFilteredList([...filteredListWorkspace]);
  };

  const FIRST_WORKSPACE_OPTION_ID = "team-name-first-workspace-option";

  const workspaceDropdownItemRender = (li, itemProps) => {
    const { dataItem } = itemProps;
    if (!dataItem?.QueueId || dataItem.QueueId !== filteredList[0]?.QueueId) {
      return li;
    }
    return React.cloneElement(li, { ...li.props, id: FIRST_WORKSPACE_OPTION_ID });
  };

  return (
    <React.Fragment>
      <Popup anchor={anchor.current} show={true} animate={true} onOpen={() => onOpen(deleteRef)} className={`${fromHeader ? "manage-project-popup-container" : ""}`} popupClass="dt-popup manage-project-popup">
        <div ref={deleteRef} tabIndex={number.ZERO} onFocus={() => onFocus(blurTimeoutRef)} onBlur={() => onBlur(blurTimeoutRef, hideOnBlur)}>
          <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
            <div className='dt-popup-title'>{ "Add " + operationalTeamName}</div>
            <span className='dt-popup-cancel-btn' title={label.CLOSE_POPUP} onClick={hideOnBlur}>
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                {icon.CLOSE}
              </Tooltip>
            </span>
          </div>
          <div className='dt-popup-body'>
            <div className='row'>
              <div className='form-group col-md-12'>
                <label htmlFor='project-name'>{label.NAME}</label>
                <Input id='project-name' type={"text"} ref={inputRef} name='projectName' value={name} onChange={handleProjectName} autoComplete='off' maxLength={kendo.TEAM_NAME_MAX_LENGTH} />
                <span className={`error-text text-danger ${!nameError && "d-none"}`}>{nameError ? nameError : quote.REQUIRED_FIELD}</span>
              </div>
              {!queueId && (
                <div className='form-group col-md-12'>
                  <label htmlFor='team-name'>{label.QUEUE_NAME}</label>
                  <DropDownList
                    id='team-name'
                    name='queue'
                    data={filteredList}
                    value={queue}
                    onChange={handleQueueChange}
                    textField='QueueName'
                    filterable={true}
                    onFilterChange={filterChangeForWorkSpace}
                    dataItemKey='QueueId'
                    defaultItem={{ QueueName: "Select Workspace" }}
                    itemRender={workspaceDropdownItemRender}
                  />
                  <span className={`error-text text-danger ${!queueError && "d-none"}`}>{queueError ? queueError : "cskm"}</span>
                </div>
              )}
            </div>
          </div>
          <div className='dt-popup-footer text-right'>
            <button className='btn btn-ghost mr-2' onClick={hideOnBlur}>
              {button.CANCEL}
            </button>
            <button type='submit' id='create-team' className='btn btn-primary' disabled={name.replace(/ /g, "").length < number.FOUR || !(queue?.QueueId || queueId)} onClick={handleCreateProject}>
              {button.CREATE}
            </button>
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default CreateProjectPopup;
